import React, { useState } from 'react';
import Button from '../ui/Button';

/**
 * Contact form component for consultation requests
 */
interface ContactFormProps {
  className?: string;
  defaultService?: string;
  defaultInquiryType?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({
  className = '',
  defaultService = '',
  defaultInquiryType = 'general',
}) => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: defaultService,
    inquiryType: defaultInquiryType,
    message: '',
  });
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  // Services array for dropdown
  const services = [
    { id: 'ai-training', name: 'AI Training & Education' },
    { id: 'ai-onboarding', name: 'Business AI Onboarding' },
    { id: 'workflow-automation', name: 'Workflow Automation' },
    { id: 'custom-ai-development', name: 'Custom AI Development' },
    { id: 'ai-optimization', name: 'AI System Optimization' },
  ];
  
  // Inquiry types array for dropdown
  const inquiryTypes = [
    { id: 'general', name: 'General Inquiry' },
    { id: 'consultation', name: 'Free Consultation Request' },
    { id: 'quote', name: 'Request a Quote' },
    { id: 'demo', name: 'Product Demo' },
    { id: 'support', name: 'Support Request' },
  ];
  
  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // In development mode, log the form data to help with testing
      if (process.env.NODE_ENV === 'development') {
        console.log('Form submission (DEV MODE):', formData);
      }
      
      // Use the test server endpoint when in development mode
      const endpoint = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001/api/contact' 
        : '/api/contact';
      
      console.log(`Submitting form to ${endpoint}...`);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      console.log(`Response received, status: ${response.status}`);
      
      // Get response text first to ensure we can debug if JSON parsing fails
      const responseText = await response.text();
      console.log(`Response text: ${responseText}`);
      
      // Try to parse as JSON
      let responseData;
      try {
        responseData = JSON.parse(responseText);
        console.log("Response data:", responseData);
      } catch (jsonError) {
        console.error("Error parsing JSON response:", jsonError);
        throw new Error(`Server error: ${responseText}`);
      }

      if (!response.ok) {
        const errorMsg = responseData?.message || responseData?.details || responseText || 'Unknown server error';
        console.error("Response not OK:", { status: response.status, errorMsg });
        throw new Error(errorMsg);
      }
      
      // Success!
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        inquiryType: 'general',
        message: '',
      });
    } catch (error) {
      setSubmitError('There was an error submitting your request. Please try again or contact us directly at hello@codemyai.com');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Success message component
  const SuccessMessage = () => (
    <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
      <svg className="w-12 h-12 text-green-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
      <h3 className="text-xl font-semibold text-green-800 mb-2">Request Received!</h3>
      <p className="text-green-700 mb-4">
        Thank you for reaching out. A member of our team will contact you within 1 business day.
      </p>
      <Button 
        variant="outline" 
        onClick={() => setSubmitSuccess(false)}
        className="text-green-700 border-green-300 hover:bg-green-50"
      >
        Submit Another Request
      </Button>
    </div>
  );
  
  // If form was successfully submitted, show success message
  if (submitSuccess) {
    return <SuccessMessage />;
  }
  
  return (
    <form onSubmit={handleSubmit} className={`space-y-6 ${className}`}>
      {/* Name field */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-1">
          Full Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          required
          value={formData.name}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:ring-primary focus:border-primary"
        />
      </div>
      
      {/* Email field */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
          Email Address *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          required
          value={formData.email}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:ring-primary focus:border-primary"
        />
      </div>
      
      {/* Company and Phone row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-neutral-700 mb-1">
            Company
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:ring-primary focus:border-primary"
          />
        </div>
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-neutral-700 mb-1">
            Phone Number
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:ring-primary focus:border-primary"
          />
        </div>
      </div>
      
      {/* Service and Inquiry Type row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="service" className="block text-sm font-medium text-neutral-700 mb-1">
            Service of Interest
          </label>
          <select
            id="service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:ring-primary focus:border-primary"
          >
            <option value="">Select a service</option>
            {services.map(service => (
              <option key={service.id} value={service.id}>
                {service.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="inquiryType" className="block text-sm font-medium text-neutral-700 mb-1">
            Inquiry Type *
          </label>
          <select
            id="inquiryType"
            name="inquiryType"
            required
            value={formData.inquiryType}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:ring-primary focus:border-primary"
          >
            {inquiryTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Message field */}
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-neutral-700 mb-1">
          Your Message *
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={5}
          value={formData.message}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:ring-primary focus:border-primary"
          placeholder="Tell us about your AI needs and goals..."
        ></textarea>
      </div>
      
      {/* Error message */}
      {submitError && (
        <div className="text-red-500 text-sm">
          {submitError}
        </div>
      )}
      
      {/* Submit button */}
      <div>
        <Button
          type="submit"
          variant="primary"
          className="w-full"
          size="lg"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Request'}
        </Button>
        <p className="text-sm text-neutral-500 mt-2 text-center">
          We'll respond to your inquiry within 1 business day.
        </p>
      </div>
    </form>
  );
};

export default ContactForm;